import 'regenerator-runtime/runtime';
import * as Sentry from '@sentry/browser';
import { CaptchaPage } from '@piplapps/pipl-ui';
import MixPanel from 'mixpanel-browser';
import { PIPL_SERVER_SECURITY_URL_PREFIX } from 'Constants/Common';
import Enums from 'Constants/Enums';
import React from 'react';
import { ReactNotifications } from 'react-notifications-component';
import NavigationUtils from 'Utils/NavigationUtils';
import 'react-notifications-component/dist/theme.css';
import 'reactjs-popup/dist/index.css';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Switch, useHistory } from 'react-router-dom';
import '../../static/less/common_refactor.less';
import styles from './App.module.less';
import AppConfig from './Config/AppConfig';
import ManagementPanel from './ManagementPanel/ManagementPanel';
import BlockPage from './Pages/BlockPage';
import EmailConfirm from './Pages/EmailConfirm';
import ExistingCustomerVettingPage from './Pages/ExistingCustomerVettingPage';
import LeadVettingPage from './Pages/LeadVettingPage';
import SignUpPage from './Pages/SignUpPage';
import OnboardingBilling from './Pages/OnboardingBilling';
import SetPasswordPage from './Pages/SetPasswordPage';
import YellowPage from './Pages/YellowMessagePage';
import LoginWithSSo from './Pages/LoginWithSSOPage';
import { store } from './Redux/store';
import './global.css';
import usePageviewAnalytics from './Hooks/usePageviewAnalytics';
import InvalidTokenPage from './Pages/InvalidTokenPage/InvalidTokenPage';
import ApiService from './Services/ApiService';
import HeaderContainer from './Components/HeaderContainer/HeaderContainer';
import DeviceIntelligenceProvider from './Components/DeviceIntelligenceProvider';
import { LocalStorage } from './Services/LocalStorageService';

// TODO: remove it after some priod of time
// localStorage data has no expiration time
// so we remove here these keys sfrom localstorage due to security issue
LocalStorage.remove('ajs_user_id');
LocalStorage.remove('ajs_anonymous_id');
LocalStorage.remove('ajs_user_traits');

if (AppConfig.ENABLE_SENTRY) {
  Sentry.init({ dsn: AppConfig.SENTRY_DSN, release: AppConfig.SENTRY_VERSION });
}

if (AppConfig.MIXPANEL_TOKEN) {
  MixPanel.init(AppConfig.MIXPANEL_TOKEN, { autotrack: true });
}

function RouterGetter() {
  const history = useHistory();

  NavigationUtils.setHistory(history);

  return null;
}

function HistoryWatcher() {
  usePageviewAnalytics();

  return null;
}

function AppRouter() {
  return (
    <Provider store={store}>
      <DeviceIntelligenceProvider />
      <ReactNotifications />
      <HeaderContainer />
      <section className={styles.container}>
        <div className="row">
          <Router>
            <RouterGetter />
            <HistoryWatcher />
            <Switch>
              <Route
                path={['/captcha', '/accounts/captcha']}
                component={() => (
                  <div className={styles.headerSpace}>
                    <CaptchaPage
                      sitekey={AppConfig.RECAPTCHA_KEY}
                      HTTPClient={ApiService.HTTPClient}
                      urlPrefix={PIPL_SERVER_SECURITY_URL_PREFIX}
                    />
                  </div>
                )}
              />
              <Route
                path={['/trial-sign-up-search', '/accounts/trial-sign-up-search']}
                component={({ history }) => (
                  <LeadVettingPage history={history} product={Enums.PRODUCT_TYPE.SEARCH_TRIAL} />
                )}
              />
              <Route
                path={['/sign-up-search', '/accounts/sign-up-search']}
                component={({ history }) => (
                  <LeadVettingPage history={history} product={Enums.PRODUCT_TYPE.SEARCH_PURCHASE} />
                )}
              />
              <Route
                path={['/trial-sign-up-api', '/accounts/trial-sign-up-api']}
                component={({ history }) => (
                  <LeadVettingPage history={history} product={Enums.PRODUCT_TYPE.API_TRIAL} />
                )}
              />
              <Route
                path={['/sign-up-api', '/accounts/sign-up-api']}
                component={({ history }) => (
                  <LeadVettingPage history={history} product={Enums.PRODUCT_TYPE.API_PURCHASE} />
                )}
              />
              <Route
                path={['/sign-up', '/accounts/sign-up']}
                component={({ history }) => <SignUpPage history={history} />}
              />
              <Route
                path={['/existing-customer-vetting', '/accounts/existing-customer-vetting']}
                component={({ history }) => (
                  <ExistingCustomerVettingPage
                    history={history}
                    isExistingCustomer
                    product={Enums.PRODUCT_TYPE.SEARCH_TRIAL}
                  />
                )}
              />

              <Route path={['/email-confirm', '/accounts/email-confirm']} component={EmailConfirm} />
              <Route path={['/block', '/accounts/block']} component={BlockPage} />
              <Route path={['/login-with-sso', '/accounts/login-with-sso']} component={LoginWithSSo} />
              <Route
                path={['/expired-token', '/accounts/expired-token']}
                component={({ history }) => <InvalidTokenPage invalidReason="expired" history={history} />}
              />
              <Route
                path={['/invalid-token', '/accounts/invalid-token']}
                component={({ history }) => <InvalidTokenPage invalidReason="invalid" history={history} />}
              />
              <Route
                path={['/used-token', '/accounts/used-token']}
                component={({ history }) => <InvalidTokenPage invalidReason="used" history={history} />}
              />
              <Route path={['/pending', '/accounts/pending']} component={YellowPage} />
              <Route path={['/set-password', '/accounts/set-password']} component={SetPasswordPage} />
              <Route
                path={['/onboard-trust', '/accounts/onboard-trust']}
                component={({ history }) => <OnboardingBilling history={history} />}
              />
              <Route
                path={['/password-expired', '/accounts/password-expired']}
                component={() => <SetPasswordPage expiredPassword />}
              />
              <Route path={['/', '/accounts']} component={ManagementPanel} />
            </Switch>
          </Router>
        </div>
      </section>
    </Provider>
  );
}

export default AppRouter;
